import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CasesService,
  CaseAcquisitionLeadOutput,
  CaseAcquisitionObservationOutput,
  CaseAcquisitionTimelineOutput,
} from '../../service/cases.service';
import {
  LeadDetails,
  LeadObservation,
  LeadTimelineEvent,
  PillTone,
  TimelineTone,
} from './lead-details-modal.types';

type LeadDetailsTab = 'info' | 'timeline' | 'observations';

const statusMap = {
  LEAD: 'Lead',
  CONTACTED_LEAD: 'Contato realizado',
  CLIENT: 'Cliente',
  LOST_LEAD: 'Lead perdido',
  PROCESS_COMPLETED_SUCCESS: 'Processo finalizado com sucesso',
  PROCESS_COMPLETED_FAILURE: 'Processo finalizado sem sucesso',
  HISTORY: 'Histórico',
} as const;

type CaseAcquisitionStatus = keyof typeof statusMap;

const statusToneMap: Record<CaseAcquisitionStatus, PillTone> = {
  LEAD: 'pill--orange',
  CONTACTED_LEAD: 'pill--blue',
  CLIENT: 'pill--green',
  LOST_LEAD: 'pill--red',
  PROCESS_COMPLETED_SUCCESS: 'pill--green',
  PROCESS_COMPLETED_FAILURE: 'pill--red',
  HISTORY: 'pill--purple',
};

@Component({
  selector: 'app-lead-details-modal',
  templateUrl: './lead-details-modal.component.html',
  styleUrls: ['./lead-details-modal.component.scss'],
})
export class LeadDetailsModalComponent implements OnInit {
  loading = true;
  leadId: number | null = null;
  observationUpdateId = '';
  activeTab: LeadDetailsTab = 'info';
  timelinePage = 1;
  timelinePageSize = 3;
  observationsPage = 1;
  observationsPageSize = 2;
  showFullDetails = true;

  lead: LeadDetails = {
    name: '',
    phone: '',
    email: '',
    source: '',
    status: '',
    statusTone: 'pill--gray',
    benefitType: '',
    previdenciaStatus: '',
    city: '',
    state: '',
    createdAt: '',
    initialObservation: '',
    isClient: false,
  };
  leadInitials = '';

  timeline: LeadTimelineEvent[] = [];
  observationHistory: LeadObservation[] = [];

  newObservation = '';

  constructor(
    public dialogRef: MatDialogRef<LeadDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: { leadId?: number },
    private readonly casesService: CasesService,
    private readonly snack: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.leadId = this.normalizeLeadId(this.data?.leadId);
    if (!this.leadId) {
      this.closeModal();
      return;
    }

    this.dialogRef.addPanelClass('lead-details-dialog');
    this.fetchLead();
  }

  closeModal(lastObservation?: string): void {
    this.dialogRef.close(lastObservation);
  }

  saveChanges(): void {
    const trimmed = this.newObservation.trim();
    if (!trimmed) {
      this.closeModal();
      return;
    }

    this.newObservation = trimmed;
    this.saveObservation();
  }

  scrollToSection(sectionId: string, tab?: LeadDetailsTab): void {
    if (tab) {
      this.activeTab = tab;
    }
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  get timelineTotalPages(): number {
    return Math.max(1, Math.ceil(this.timeline.length / this.timelinePageSize));
  }

  get observationTotalPages(): number {
    return Math.max(1, Math.ceil(this.observationHistory.length / this.observationsPageSize));
  }

  get pagedTimeline(): LeadTimelineEvent[] {
    const start = (this.timelinePage - 1) * this.timelinePageSize;
    return this.timeline.slice(start, start + this.timelinePageSize);
  }

  get pagedObservations(): LeadObservation[] {
    const start = (this.observationsPage - 1) * this.observationsPageSize;
    return this.observationHistory.slice(start, start + this.observationsPageSize);
  }

  get leadLocation(): string {
    const city = this.lead.city?.trim() || '';
    const state = this.lead.state?.trim() || '';
    if (city && state) {
      return `${city} / ${state}`;
    }
    return city || state || '';
  }

  changeTimelinePage(delta: number): void {
    const next = this.timelinePage + delta;
    this.timelinePage = Math.max(1, Math.min(this.timelineTotalPages, next));
  }

  changeObservationsPage(delta: number): void {
    const next = this.observationsPage + delta;
    this.observationsPage = Math.max(1, Math.min(this.observationTotalPages, next));
  }

  private fetchLead(): void {
    if (!this.leadId) {
      return;
    }
    this.loading = true;

    this.casesService.getLeadById(this.leadId).subscribe({
      next: (response) => {
        this.applyLeadResponse(response);
        if (this.showFullDetails) {
          this.fetchTimeline();
          this.fetchObservations();
        } else {
          this.timeline = [];
          this.observationHistory = [];
          this.timelinePage = 1;
          this.observationsPage = 1;
          this.activeTab = 'info';
        }
        this.loading = false;
      },
      error: (err) => {
        console.log('Erro ao buscar lead:', err);
        this.loading = false;
      },
    });
  }

  private fetchObservations(): void {
    if (!this.leadId) {
      return;
    }
    this.casesService.listLeadObservations(this.leadId).subscribe({
      next: (response) => {
        const items = this.normalizeArray<CaseAcquisitionObservationOutput>(response);
        this.observationHistory = items.map((item) => this.mapObservationResponse(item));
        this.observationsPage = 1;
      },
      error: (err) => {
        console.log('Erro ao listar observacoes:', err);
      },
    });
  }

  private fetchTimeline(): void {
    if (!this.leadId) {
      return;
    }
    this.casesService.listLeadTimeline(this.leadId).subscribe({
      next: (response) => {
        const items = this.normalizeArray<CaseAcquisitionTimelineOutput>(response);
        this.timeline = items.map((item) => this.mapTimelineResponse(item));
        this.timelinePage = 1;
      },

      error: (err) => {
        console.log('Erro ao listar linha do tempo:', err);
      },
    });
  }

  private saveObservation(): void {
    if (!this.leadId) {
      return;
    }
    const trimmed = this.newObservation.trim();
    if (!trimmed) {
      return;
    }

    if (this.isValidUuid(this.observationUpdateId)) {
      this.updateObservation(trimmed);
      return;
    }

    this.createObservation(trimmed);
  }

  private createObservation(text: string): void {
    this.casesService.createLeadObservation(this.leadId, text).subscribe({
      next: () => {
        this.newObservation = '';
        this.fetchObservations();
        this.snack.open('Observação salva com sucesso.', 'Fechar', {
          duration: 3000,
        });
        this.closeModal(text);
      },
      error: (err) => {
        console.log('Erro ao criar observacao:', err);
      },
    });
  }

  private updateObservation(text: string): void {
    this.casesService.updateLeadObservation(this.leadId, this.observationUpdateId, text).subscribe({
      next: () => {
        this.newObservation = '';
        this.fetchObservations();
        this.snack.open('Observação salva com sucesso.', 'Fechar', {
          duration: 3000,
        });
        this.closeModal();
      },
      error: (err) => {
        console.log('Erro ao atualizar observacao:', err);
      },
    });
  }

  private applyLeadResponse(response: CaseAcquisitionLeadOutput): void {
    const patient = response.casePatientId;
    const contacts = Array.isArray(patient?.contacts) ? patient.contacts : [];
    const leadInfo = response.lead;
    const rawStatus = leadInfo?.status || leadInfo?.statusLabel || '';
    const resolvedStatus = this.resolveStatusMetadata(rawStatus);
    const createdAt = this.formatDateTime(leadInfo?.createdAt);
    const isClient = this.inferIsClient(rawStatus);
    this.showFullDetails = !this.isHistoryStatus(rawStatus);
    const name = patient?.name || '';

    this.lead = {
      ...this.lead,
      name,
      phone: this.pickContactValue(contacts, ['whatsapp', 'celular', 'telefone']),
      email: this.pickContactValue(contacts, ['email', 'e-mail']),
      source: 'Central de Oportunidades',
      status: resolvedStatus.label || rawStatus,
      statusTone: resolvedStatus.tone,
      benefitType: response.benefitType || '',
      previdenciaStatus: response.inssStatus || '',
      city: patient?.city || '',
      state: patient?.state || '',
      createdAt,
      initialObservation: response.observation || '',
      isClient,
    };
    this.leadInitials = this.getLeadInitials(name);
  }

  applyWaLink(number: string) {
    return `https://wa.me/55${number}`;
  }

  private mapObservationResponse(response: CaseAcquisitionObservationOutput): LeadObservation {
    const payload = response as Record<string, any>;
    const author = this.pickText(payload.userName, 'Sistema');
    const date = this.formatDateTime(payload.createdAt);
    const text = this.pickText(
      payload.observation,
      payload.text,
      payload.note,
      payload.description
    );

    return {
      id: this.pickText(payload.id, payload.uuid, payload.observationId),
      author: author || 'Sistema',
      date: date || '',
      text,
    };
  }

  private mapTimelineResponse(response: CaseAcquisitionTimelineOutput): LeadTimelineEvent {
    const eventType = response.eventType || '';
    const previousValue = response.previousValue || '';
    const newValue = response.newValue || '';
    const metadata = response.metadata || {};
    const previousStatus = this.resolveStatusMetadata(previousValue, '-');
    const nextStatus = this.resolveStatusMetadata(newValue, '-');

    return {
      id: response.id || '',
      title: eventType || 'Atualizacao',
      detail: this.getTimelineDetail(metadata),
      user: response.userName || 'Sistema',
      date: this.formatDateTime(response.createdAt),
      relative: '',
      tone: this.getTimelineTone(eventType, newValue),
      previousStatus: previousStatus.label,
      previousStatusTone: previousStatus.tone,
      newStatus: nextStatus.label,
      newStatusTone: nextStatus.tone,
    };
  }

  private pickText(...values: Array<string | null | undefined>): string {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
    return '';
  }

  private pickContactValue(
    contacts: Array<{ contactMethod?: string; contact?: string; primaryContact?: boolean }>,
    methods: string[]
  ): string {
    if (!contacts.length) {
      return '';
    }

    const normalizedMethods = methods.map((method) => method.toLowerCase());
    const primaryMatch = contacts.find((contact) => contact.primaryContact);
    if (primaryMatch?.contact && this.matchContactMethod(primaryMatch, normalizedMethods)) {
      return primaryMatch.contact;
    }

    const match = contacts.find((contact) => this.matchContactMethod(contact, normalizedMethods));
    return match?.contact || '';
  }

  private getLeadInitials(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) {
      return '';
    }

    const words = trimmed.split(/\s+/).filter((word) => !/^(de|do|da|dos|das)$/i.test(word));

    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }

    if (words.length === 1) {
      const [word] = words;
      if (word.length >= 2) {
        return (word.charAt(0) + word.charAt(1)).toUpperCase();
      }
      return word.toUpperCase();
    }

    const compact = trimmed.replace(/\s+/g, '');
    if (compact.length >= 2) {
      return (compact.charAt(0) + compact.charAt(1)).toUpperCase();
    }
    return compact.toUpperCase();
  }

  private matchContactMethod(contact: { contactMethod?: string }, methods: string[]): boolean {
    const method = contact.contactMethod?.toLowerCase() ?? '';
    return methods.some((target) => method.includes(target));
  }

  private normalizeArray<T>(value: T[] | { data?: T[] } | null | undefined): T[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (value?.data && Array.isArray(value.data)) {
      return value.data;
    }
    return [];
  }

  private formatDateTime(value: unknown): string {
    if (!value) {
      return '';
    }

    if (value instanceof Date) {
      return this.formatDate(value);
    }

    if (typeof value === 'number') {
      return this.formatDate(new Date(value));
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d+$/.test(trimmed)) {
        return this.formatDate(new Date(Number(trimmed)));
      }
      const parsed = Date.parse(trimmed);
      if (!Number.isNaN(parsed)) {
        return this.formatDate(new Date(parsed));
      }
      return value;
    }

    return '';
  }

  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private inferIsClient(status: string): boolean {
    const normalized = this.normalizeText(status);
    if (
      normalized.includes('virou cliente') ||
      normalized.includes('cliente') ||
      normalized.includes('client')
    ) {
      return true;
    }
    return false;
  }

  private isHistoryStatus(status: string): boolean {
    const normalizedKey = this.normalizeStatusKey(status);
    if (normalizedKey === 'HISTORY') {
      return true;
    }
    const normalizedText = this.normalizeText(status);
    return normalizedText.includes('history') || normalizedText.includes('historico');
  }

  private getStatusTone(status: string): PillTone {
    const normalized = this.normalizeText(status);
    if (
      normalized.includes('perdido') ||
      normalized.includes('lost') ||
      normalized.includes('falha') ||
      normalized.includes('failure') ||
      normalized.includes('sem sucesso')
    ) {
      return 'pill--red';
    }
    if (
      normalized.includes('cliente') ||
      normalized.includes('client') ||
      normalized.includes('sucesso') ||
      normalized.includes('success')
    ) {
      return 'pill--green';
    }
    if (normalized.includes('contato') || normalized.includes('contact')) {
      return 'pill--blue';
    }
    if (normalized.includes('novo') || normalized.includes('novo lead')) {
      return 'pill--orange';
    }
    if (normalized.includes('historico') || normalized.includes('history')) {
      return 'pill--purple';
    }
    return 'pill--gray';
  }

  private getTimelineTone(eventType: string, newValue: string): TimelineTone {
    const normalizedType = this.normalizeText(eventType);
    const normalizedStatus = this.normalizeText(newValue);
    if (
      normalizedType.includes('observacao') ||
      normalizedType.includes('observation') ||
      normalizedType.includes('contato') ||
      normalizedType.includes('contact') ||
      normalizedStatus.includes('contato') ||
      normalizedStatus.includes('contact')
    ) {
      return 'tone--orange';
    }
    if (
      normalizedType.includes('perdido') ||
      normalizedType.includes('lost') ||
      normalizedType.includes('falha') ||
      normalizedType.includes('failure') ||
      normalizedStatus.includes('perdido') ||
      normalizedStatus.includes('lost') ||
      normalizedStatus.includes('falha') ||
      normalizedStatus.includes('failure') ||
      normalizedStatus.includes('sem sucesso')
    ) {
      return 'tone--red';
    }
    if (
      normalizedType.includes('sucesso') ||
      normalizedType.includes('success') ||
      normalizedType.includes('finalizado') ||
      normalizedType.includes('concluido') ||
      normalizedStatus.includes('cliente') ||
      normalizedStatus.includes('client') ||
      normalizedStatus.includes('success') ||
      normalizedStatus.includes('sucesso')
    ) {
      return 'tone--green';
    }
    if (
      normalizedType.includes('historico') ||
      normalizedType.includes('history') ||
      normalizedType.includes('sistema')
    ) {
      return 'tone--purple';
    }
    if (
      normalizedType.includes('assumido') ||
      normalizedType.includes('lead') ||
      normalizedType.includes('acquired') ||
      normalizedStatus.includes('lead')
    ) {
      return 'tone--blue';
    }
    return 'tone--gray';
  }

  private getTimelineDetail(metadata: { observation?: string }): string {
    return metadata?.observation || '';
  }

  private resolveStatusMetadata(value: string, emptyLabel = ''): { label: string; tone: PillTone } {
    if (!value) {
      return { label: emptyLabel, tone: 'pill--gray' };
    }

    const normalized = this.normalizeStatusKey(value);
    if (normalized) {
      return {
        label: statusMap[normalized],
        tone: statusToneMap[normalized],
      };
    }

    const label = this.formatStatusLabel(value);
    return {
      label,
      tone: this.getStatusTone(label),
    };
  }

  private normalizeStatusKey(value: string): CaseAcquisitionStatus | null {
    const normalized = this.normalizeText(value)
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if ((normalized as CaseAcquisitionStatus) in statusMap) {
      return normalized as CaseAcquisitionStatus;
    }
    return null;
  }

  private formatStatusLabel(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    return trimmed
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private normalizeText(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private isValidUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  private normalizeLeadId(value: number | undefined): number | null {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
    return null;
  }
}
